import React, { memo } from "react";
import Tag from "../ui/tags";
import "../../css/table.css";

/**
 * A reusable table component for displaying data.
 * 
 * @param {Object} props - The props passed to the component.
 * @param {Array<Object>} props.data - The array of data objects to be displayed in the table. Each object represents a row, with keys corresponding to column accessors.
 * @param {Array<Object>} props.columns - The array of column configuration objects. Each object should have:
 *   - `accessor` (string): The key in the data object for this column's value.
 *   - `header` (string): The text to display in the column header.
 * @param {string} props.noDataMsg - Message to display when there is no data or columns.
 * @param {Function} props.onRowClick - Callback function to handle row clicks.
 * 
 * @returns {JSX.Element} A table element populated with data and headers.
 */
const Table = ({ data, columns, onRowClick, noDataMsg = "No data or columns provided." }) => {
    if (!Array.isArray(data) || !Array.isArray(columns)) return <p>Invalid format</p>;

    if (columns.length === 0) return <p>{noDataMsg}</p>;

    const renderWithCommas = (values) => {
        if (!Array.isArray(values) || values.every(value => React.isValidElement(value) && value.type === Tag)) return values;

        return values.map((value, index) => (
            <span key={index}>
                {value}
                {index < values.length - 1 && ", "}
            </span>
        ));
    };

    return (
        <div className="table-container">  
            <table className="data-table">
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th key={col.accessor}>
                                {col.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, rowIndex) => (
                        <tr key={rowIndex} onClick={() => onRowClick && onRowClick(row)}>
                            {columns.map((col) => (
                                <td key={col.accessor} className={row[col.accessor]?.length > 50 ? "long-text" : ""}>
                                    {Array.isArray(row[col.accessor])
                                        ? renderWithCommas(row[col.accessor])
                                        : col.render
                                        ? col.render(row)
                                        : row[col.accessor]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default memo(Table);
